import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Plus, Trash2, Loader2, Database, MapPin, BarChart3, Calendar, Filter, Tag, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { datasetApi } from '../../../api/datasetApi';
import { spatialApi } from '../../../api/spatialApi';
import { workspaceApi } from '../../../api/workspaceApi';
import { cn } from '../../../lib/utils';
import { getDefaultPalette } from '../../../utils/colorPalettes';
import {
  MAP_NO_METRIC,
  metricColumnFromSpec,
  isNumericColumn,
  buildQueryValue,
  defaultAggregationForColumn
} from '../../../utils/mapLayerMetricUtils';
import { extractPersistableLayerConfig } from '../../../utils/mapLayerConfig';
import { setDatasetSchema } from '../../../utils/datasetSchemaCache';
import { ShieldCheck, Search, ChevronDown } from 'lucide-react';

const SpatialSelector = ({ columnName, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (isOpen) {
        const res = await spatialApi.searchProjections(query);
        setResults(res);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, isOpen]);

  return (
    <div className="space-y-2 p-4 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800/50">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Projection (CRS)</span>
        <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100/50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
           <ShieldCheck size={10} />
           <span className="text-[8px] font-black uppercase tracking-widest">EPSG Supported</span>
        </div>
      </div>

      <div className="relative">
        <button
          onClick={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
          className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-xl text-left hover:border-emerald-500/50 transition-all shadow-sm group"
        >
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/50 text-emerald-600 rounded-lg group-hover:scale-110 transition-transform">
                <Search size={14} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-tight truncate">
                  {value?.name || `EPSG:${value?.source_srid || value?.srid || 4326}`}
              </div>
              <div className="text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Source: EPSG:{value?.source_srid || value?.srid || 4326}</div>
            </div>
          </div>
          <ChevronDown size={14} className={cn("text-gray-300 transition-transform", isOpen && "rotate-180")} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 p-2 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl z-[50] animate-in fade-in slide-in-from-top-2 border-emerald-500/20">
            <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                <input
                autoFocus
                className="w-full pl-8 pr-3 py-2 text-[10px] font-black uppercase tracking-tight bg-gray-50 dark:bg-gray-900 rounded-lg outline-none focus:ring-1 ring-emerald-500/50 border-none transition-all placeholder:text-gray-400"
                placeholder="Search CRS..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                />
            </div>
            <div className="max-h-60 overflow-y-auto space-y-0.5 custom-scrollbar pr-1">
              {results.length === 0 && query && (
                  <div className="p-3 text-center text-[9px] font-bold text-gray-400 uppercase tracking-widest">No results</div>
              )}
              {results.map(r => (
                <button
                  key={r.srid}
                  onClick={(e) => {
                    e.preventDefault();
                    onChange({ ...value, source_srid: r.srid, name: r.name });
                    setIsOpen(false);
                  }}
                  className="w-full text-left p-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-all border border-transparent hover:border-emerald-100 dark:hover:border-emerald-500/10 group"
                >
                  <div className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-tight group-hover:text-emerald-500 transition-colors truncate">{r.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                      <div className="text-[7px] text-gray-400 font-bold uppercase tracking-widest bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded-sm">{r.area}</div>
                      <div className="text-[7px] text-emerald-500 font-black uppercase tracking-widest">EPSG:{r.srid}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const STEPS = {
  DATASET: 0,
  SPATIAL: 1,
  TIME: 2,
  METRIC: 3,
  STYLE: 4
};

const MapLayerBuilder = ({ onSave, onPreview, onCancel, initialState = null, availableDatasets = [], workspaceId, chatId }) => {
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [columns, setColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [spatialMeta, setSpatialMeta] = useState([]); // Array of SpatialSpec

  // Form State
  const [selectedDatasetId, setSelectedDatasetId] = useState(initialState?.dataset || '');
  // Track current step for sequential reveal
  const [currentStep, setCurrentStep] = useState(initialState ? STEPS.STYLE : STEPS.DATASET);

  const [form, setForm] = useState({
    spatialColumn: initialState?.spatial?.column || '',
    spatialSrid: initialState?.spatial?.srid || 4326,
    spatialName: initialState?.spatial?.name || '',
    valueColumn: metricColumnFromSpec(initialState?.value),
    aggregation: initialState?.value?.aggregation || 'sum',
    filters: initialState?.filters || [],
    palette: initialState?.config?.palette || getDefaultPalette(), // Default to viridis
    inverted: initialState?.config?.inverted || false
  });

  // Time Config State
  const [timeConfig, setTimeConfig] = useState({
    column: initialState?.time?.column || '',
    mode: initialState?.time?.start && initialState?.time?.end && initialState?.time?.start === initialState?.time?.end ? 'single' : 'range',
    start: initialState?.time?.start ? initialState.time.start.split('T')[0] : '',
    end: initialState?.time?.end ? initialState.time.end.split('T')[0] : '',
    singleDate: initialState?.time?.start ? initialState.time.start.split('T')[0] : '',
    minDate: '',
    maxDate: ''
  });


  // Autocomplete Options & Loading State
  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilters, setLoadingFilters] = useState({});

  // Time distinct values for single date mode
  const [timeDistinctValues, setTimeDistinctValues] = useState([]);
  const [loadingTimeValues, setLoadingTimeValues] = useState(false);

  // Artifact saving state
  const [savingArtefact, setSavingArtefact] = useState(false);
  const [gallerySaveError, setGallerySaveError] = useState(null);
  const [layerName, setLayerName] = useState(initialState?.name || '');

  // Propagate default name if empty
  useEffect(() => {
    if (!layerName && selectedDatasetId && form.valueColumn) {
      const desc = form.valueColumn === MAP_NO_METRIC
        ? 'Spatial layer'
        : `${form.aggregation.toUpperCase()} (${form.valueColumn})`;
      setLayerName(desc);
    }
  }, [selectedDatasetId, form.valueColumn, form.aggregation, layerName]);

  // Load Datasets
  useEffect(() => {
    const loadDatasets = async () => {
      // If we have availableDatasets from the workspace, use them!
      if (availableDatasets && availableDatasets.length > 0) {
        // Map available datasets to the format expected by the builder
        // The workspace sources have { id, name, type } which usually matches.
        setDatasets(availableDatasets);
        // Select first dataset by default if not editing and no dataset selected
        if (!initialState && availableDatasets.length > 0) {
          setSelectedDatasetId(prev => prev || availableDatasets[0].id);
        }
        return;
      }

      setLoadingDatasets(true);
      try {
        const data = await datasetApi.getDatasets();
        setDatasets(data);
        // Select first dataset by default if not editing and no dataset selected
        if (!initialState && data.length > 0) {
          setSelectedDatasetId(prev => prev || data[0].id);
        }
      } catch (err) {
        console.error('Failed to load datasets:', err);
      } finally {
        setLoadingDatasets(false);
      }
    };
    loadDatasets();
  }, [availableDatasets, initialState]);

  // Helper to determine input type
  const getColType = (colName, cols = columns) => {
    const col = cols.find(c => c.name === colName);
    return col ? col.type : 'STRING';
  };

  const hasMetric = form.valueColumn && form.valueColumn !== MAP_NO_METRIC;
  const metricIsNumeric = hasMetric && isNumericColumn(form.valueColumn, columns);
  const metricReady = form.spatialColumn && timeConfig.column && form.valueColumn;

  const handleTimeColumnChange = React.useCallback(async (colName) => {
    setTimeConfig(prev => ({ ...prev, column: colName }));
    setTimeDistinctValues([]);
    if (!colName) {
      setCurrentStep(prev => prev < STEPS.METRIC ? STEPS.METRIC : prev);
      return;
    }
    // Advance to metric step after selecting time column
    setCurrentStep(prev => prev < STEPS.METRIC ? STEPS.METRIC : prev);

    try {
      const range = await datasetApi.getColumnRange(selectedDatasetId, colName);
      setTimeConfig(prev => ({
        ...prev,
        minDate: range.min_val,
        maxDate: range.max_val,
        // Only override values if they are empty
        start: prev.start || (range.min_val ? range.min_val.split('T')[0] : ''),
        end: prev.end || (range.max_val ? range.max_val.split('T')[0] : ''),
        singleDate: prev.singleDate || (range.min_val ? range.min_val.split('T')[0] : '')
      }));
    } catch (err) {
      console.error('Failed to load time range:', err);
    }
  }, [selectedDatasetId]);

  // Load Columns & Auto-Select
  useEffect(() => {
    if (!selectedDatasetId) {
      setColumns([]);
      return;
    }
    const loadMetadata = async () => {
      setLoadingColumns(true);
      try {
        const meta = await datasetApi.getDatasetMetadata(selectedDatasetId);
        const cols = meta.schema || [];
        setColumns(cols);
        setDatasetSchema(selectedDatasetId, cols);
        setSpatialMeta(meta.spatial_columns || []);

        // Auto-selection only if not editing (or if empty)
        if (!initialState) {
          const geomMeta = (meta.spatial_columns || []).find(
            (sc) => sc.type === 'geometry' && sc.name === 'geometry'
          );
          const geomCol = geomMeta
            ? cols.find((c) => c.name === 'geometry')
            : null;
          const h3Meta = (meta.spatial_columns || []).find((sc) => sc.type === 'h3');
          const h3Col = h3Meta
            ? cols.find((c) => c.name === h3Meta.name)
            : cols.find(
                (c) =>
                  c.name.toLowerCase().includes('h3') ||
                  c.name.toLowerCase().includes('hex')
              );
          if (geomCol) {
            setForm((f) => ({
              ...f,
              spatialColumn: geomCol.name,
              spatialSrid: geomMeta?.srid ?? 4326,
              spatialSourceSrid: geomMeta?.source_srid ?? geomMeta?.srid ?? 4326,
            }));
            setCurrentStep((prev) => (prev < STEPS.TIME ? STEPS.TIME : prev));
          } else if (h3Col) {
            setForm((f) => ({ ...f, spatialColumn: h3Col.name }));
            setCurrentStep((prev) => (prev < STEPS.TIME ? STEPS.TIME : prev));
          } else {
            setCurrentStep((prev) => (prev < STEPS.SPATIAL ? STEPS.SPATIAL : prev));
          }

          // 2. Auto-detect Time column
          const timeCol = cols.find(c =>
            c.name.toLowerCase().includes('date') ||
            c.name.toLowerCase().includes('time') ||
            c.name.toLowerCase().includes('timestamp')
          );
          if (timeCol) {
            // Auto-select time column and load its range
            handleTimeColumnChange(timeCol.name);
            // Advance to time step
            setCurrentStep(prev => prev < STEPS.TIME ? STEPS.TIME : prev);
          }
        } else {
          // Restore time bounds if editing
          if (initialState.time?.column) {
            // We need to fetch the range to populate the slider bounds
            datasetApi.getColumnRange(selectedDatasetId, initialState.time.column)
              .then(range => {
                setTimeConfig(prev => ({
                  ...prev,
                  minDate: range.min_val,
                  maxDate: range.max_val
                }));
              });
          }
        }
      } catch (err) {
        console.error('Failed to load columns:', err);
      } finally {
        setLoadingColumns(false);
      }
    };
    loadMetadata();
  }, [selectedDatasetId, initialState, handleTimeColumnChange]);


  // Load distinct date values when switching to single date mode
  const loadTimeDistinctValues = async () => {
    if (!timeConfig.column) return;

    setLoadingTimeValues(true);
    try {
      const values = await datasetApi.getDistinctValues(selectedDatasetId, timeConfig.column);
      // Sort and format dates
      const sortedDates = values
        .map(v => {
          // Handle different date formats
          const dateStr = typeof v === 'string' ? v.split('T')[0] : v;
          return dateStr;
        })
        .filter(v => v)
        .sort();

      setTimeDistinctValues(sortedDates);

      // Set first date as default if singleDate is not set
      if (sortedDates.length > 0 && !timeConfig.singleDate) {
        setTimeConfig(prev => ({ ...prev, singleDate: sortedDates[0] }));
      }
    } catch (err) {
      console.error('Failed to load distinct time values:', err);
    } finally {
      setLoadingTimeValues(false);
    }
  };

  // Load distinct values when switching to single date mode
  useEffect(() => {
    if (timeConfig.mode === 'single' && timeConfig.column && !loadingTimeValues) {
      // Only load if we don't have values yet
      if (timeDistinctValues.length === 0) {
        loadTimeDistinctValues();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeConfig.mode, timeConfig.column]);


  // --- Filter Logic ---
  const handleAddFilter = () => {
    setForm(prev => ({
      ...prev,
      filters: [...prev.filters, { column: '', op: '=', values: [''] }]
    }));
  };

  const updateFilter = (index, field, value) => {
    // Auto-load options if column changes
    if (field === 'column') {
      loadFilterOptions(index, value);
      // If switching to a STRING column, force operator to "="
      const colType = getColType(value);
      if (colType === 'STRING') {
        setForm(prev => {
          const newFilters = [...prev.filters];
          newFilters[index] = { ...newFilters[index], [field]: value, op: '=' };
          return { ...prev, filters: newFilters };
        });
        return;
      }
    }
    setForm(prev => {
      const newFilters = [...prev.filters];
      newFilters[index] = { ...newFilters[index], [field]: value };
      return { ...prev, filters: newFilters };
    });
  };

  const removeFilter = (index) => {
    setForm(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  const loadFilterOptions = async (idx, colName) => {
    const type = getColType(colName);
    if (type !== 'STRING' && type !== 'object') return;

    setLoadingFilters(prev => ({ ...prev, [idx]: true }));
    try {
      const values = await datasetApi.getDistinctValues(selectedDatasetId, colName);
      setFilterOptions(prev => ({ ...prev, [idx]: values }));
    } catch (err) {
      console.error('Failed to load filter options:', err);
    } finally {
      setLoadingFilters(prev => ({ ...prev, [idx]: false }));
    }
  };

  // --- Submission ---
  const constructPayload = () => {
    if (!selectedDatasetId || !form.spatialColumn || !form.valueColumn || !timeConfig.column) return null;

    const spatialEntry = spatialMeta.find((sm) => sm.name === form.spatialColumn);
    const geometryType =
      spatialEntry?.type === 'geometry'
        ? 'polygon'
        : spatialEntry?.type === 'h3'
          ? 'h3'
          : form.spatialColumn?.toLowerCase().includes('h3')
            ? 'h3'
            : 'polygon';

    const payload = {
      dataset: selectedDatasetId,
      spatial: {
        geometry_type: geometryType,
        column: form.spatialColumn,
        srid: 4326,
        source_srid: spatialMeta.some((sm) => sm.name === form.spatialColumn)
          ? form.spatialSourceSrid || form.spatialSrid
          : undefined,
      },
      dimensions: [],
      filters: form.filters
        .filter(f => f.column && f.values[0])
        .map(f => ({
          column: f.column,
          op: f.op,
          values: Array.isArray(f.values) ? f.values : [f.values]
        }))
    };

    payload.value = buildQueryValue(form.valueColumn, form.aggregation);

    // Time is always required
    if (timeConfig.column) {
      const colType = getColType(timeConfig.column);
      const isDateType = colType === 'DATE';

      if (timeConfig.mode === 'range' && timeConfig.start && timeConfig.end) {
        payload.time = {
          column: timeConfig.column,
          start: timeConfig.start,
          end: timeConfig.end
        };
        if (!isDateType) {
          payload.time.start = `${timeConfig.start}T00:00:00`;
          payload.time.end = `${timeConfig.end}T23:59:59`;
        }

      } else if (timeConfig.mode === 'single' && timeConfig.singleDate) {
        if (isDateType) {
          payload.time = {
            column: timeConfig.column,
            start: timeConfig.singleDate,
            end: timeConfig.singleDate
          };
        } else {
          payload.time = {
            column: timeConfig.column,
            start: `${timeConfig.singleDate}T00:00:00`,
            end: `${timeConfig.singleDate}T23:59:59`
          };
        }
      }
    }

    return payload;
  };



  const handleSubmit = async () => {
    const payload = constructPayload();
    if (!payload) return;

    const layerConfig = {
      ...extractPersistableLayerConfig(initialState?.config || {}),
      palette: form.palette,
      inverted: form.inverted,
    };

    const specFromInitial = initialState
      ? Object.keys(payload).reduce((acc, k) => ({ ...acc, [k]: initialState[k] }), {})
      : null;
    const specChanged =
      !initialState || JSON.stringify(payload) !== JSON.stringify(specFromInitial);
    const configChanged =
      !initialState ||
      JSON.stringify(layerConfig) !== JSON.stringify(initialState.config || {});

    // Check if anything meaningful has changed before pushing a save to the gallery
    const hasChanged =
      !initialState ||
      layerName !== initialState.name ||
      specChanged ||
      configChanged;

    let savedLayerId = initialState?.layerId ? String(initialState.layerId) : null;

    if (hasChanged) {
      setGallerySaveError(null);
      setSavingArtefact(true);
      try {
        if (savedLayerId) {
          await workspaceApi.updateArtefact(
            savedLayerId,
            {
              title: layerName,
              spec: payload,
              config: layerConfig,
            },
            { suppressGlobalErrorToast: true }
          );
        } else {
          const result = await workspaceApi.saveArtefact(
            workspaceId,
            {
              title: layerName,
              type: 'map',
              spec: payload,
              config: layerConfig,
              chat_id: chatId || null,
            },
            { suppressGlobalErrorToast: true }
          );
          savedLayerId = String(result.id);
        }
      } catch (err) {
        console.error('Failed to auto-save to gallery:', err);
        const detail = err.response?.data?.detail;
        const msg =
          (typeof detail === 'string' && detail) ||
          err.message ||
          'Failed to save to gallery';
        setGallerySaveError(`Layer was updated on the map, but could not be saved to the gallery: ${msg}`);
      } finally {
        setSavingArtefact(false);
      }
    }

    // Always notify parent to update local state/map, passing the stable layerId if it exists
    onSave(payload, layerName, layerConfig, savedLayerId);
  };


  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 pb-20"> {/* pb-20 for fixed footer */}

      {gallerySaveError && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs font-bold text-amber-800 dark:text-amber-200 flex-1">{gallerySaveError}</p>
          <button
            type="button"
            onClick={() => setGallerySaveError(null)}
            className="p-1 hover:bg-amber-100 dark:hover:bg-amber-800/50 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X size={14} className="text-amber-600" />
          </button>
        </div>
      )}

      {/* 1. DATASET */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
          <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
            <Database size={12} />
          </div>
          Dataset <span className="text-red-500 font-normal normal-case">(Required)</span>
        </label>
        <div className="relative">
          <select
            value={selectedDatasetId}
            onChange={(e) => {
              setSelectedDatasetId(e.target.value);
              setTimeConfig({
                column: '',
                mode: 'range',
                start: '',
                end: '',
                singleDate: '',
                minDate: '',
                maxDate: ''
              });
              if (currentStep < STEPS.SPATIAL) setCurrentStep(STEPS.SPATIAL);
            }}
            required
            className={cn(
              "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
              !selectedDatasetId
                ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
            )}
            disabled={loadingDatasets || (initialState && true)}
          >
            <option value="">Select Dataset...</option>
            {datasets.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <Database size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* 2. SPATIAL */}
      {selectedDatasetId && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
              <MapPin size={12} />
            </div>
            Spatial Index (H3) <span className="text-red-500 font-normal normal-case">(Required)</span>
          </label>
          {currentStep >= STEPS.SPATIAL && (() => {
            // Filter to only spatial columns (H3 or native spatial from meta)
            const spatialColumns = columns.filter(c =>
              c.name.toLowerCase().includes('h3') ||
              c.name.toLowerCase().includes('hex') ||
              spatialMeta.some(sm => sm.name === c.name)
            );
            const hasSpatialColumns = spatialColumns.length > 0;

            return (
              <div className="space-y-2">
                {!hasSpatialColumns && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-amber-900 dark:text-amber-200 uppercase tracking-tight">
                        No Spatial Columns Found
                      </p>
                      <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                        This dataset doesn't contain any spatial columns (H3, geo, spatial, location, or geometry). A spatial column is required to create a map layer.
                      </p>
                    </div>
                  </div>
                )}
                <div className="relative">
                  <select
                    value={form.spatialColumn}
                    onChange={(e) => {
                      const colName = e.target.value;
                      const sm = spatialMeta.find(s => s.name === colName);
                      setForm(f => ({
                        ...f,
                        spatialColumn: colName,
                        spatialSrid: 4326,
                        spatialSourceSrid: sm ? sm.source_srid || sm.srid || 4326 : 4326,
                        spatialName: sm ? (sm.srid === 2100 ? "Greek Grid (EPSG:2100)" : sm.srid === 4326 ? "WGS 84 (Global)" : `EPSG:${sm.srid}`) : ''
                      }));
                      setCurrentStep(prev => prev < STEPS.TIME ? STEPS.TIME : prev);
                    }}
                    required
                    className={cn(
                      "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                      !form.spatialColumn
                        ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                        : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                    )}
                    disabled={loadingColumns || !hasSpatialColumns}
                  >
                    <option value="">{hasSpatialColumns ? "Select Column..." : "No Spatial Columns Available"}</option>
                    {spatialColumns.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                {spatialMeta.some(sm => sm.name === form.spatialColumn) && (
                  <div className="mt-3 animate-in fade-in slide-in-from-top-2">
                    <SpatialSelector
                      columnName={form.spatialColumn}
                      value={{ srid: form.spatialSrid, source_srid: form.spatialSourceSrid, name: form.spatialName }}
                      onChange={(val) => setForm(f => ({
                        ...f,
                        spatialSourceSrid: val.source_srid || val.srid,
                        spatialName: val.name
                      }))}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* 3. TIME */}
      {form.spatialColumn && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
              <Calendar size={12} />
            </div>
            Time <span className="text-red-500 font-normal normal-case">(Required)</span>
          </label>
          {currentStep >= STEPS.TIME && (
            <div className="space-y-3">
              <div className="relative">
                <select
                  value={timeConfig.column}
                  onChange={(e) => handleTimeColumnChange(e.target.value)}
                  required
                  className={cn(
                    "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                    !timeConfig.column
                      ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                      : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  )}
                >
                  <option value="">Select Time Column...</option>
                  {columns.filter(c =>
                    c.name.toLowerCase().includes('date') ||
                    c.name.toLowerCase().includes('time') ||
                    c.name.toLowerCase().includes('timestamp')
                  ).map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              {timeConfig.column && (
                <div className="p-4 bg-gradient-to-br from-gray-50/50 to-white dark:from-gray-900/30 dark:to-gray-900/50 rounded-2xl border-2 border-gray-100 dark:border-gray-800 space-y-4 shadow-sm">
                  <div className="flex bg-white dark:bg-gray-900 rounded-xl border-2 border-gray-100 dark:border-gray-800 p-1 shadow-sm">
                    <button
                      onClick={() => setTimeConfig(t => ({ ...t, mode: 'range' }))}
                      className={cn(
                        "flex-1 text-xs font-bold py-2.5 rounded-lg transition-all uppercase tracking-wider",
                        timeConfig.mode === 'range'
                          ? "bg-emerald-500 text-white shadow-md"
                          : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      Date Range
                    </button>
                    <button
                      onClick={() => {
                        setTimeConfig(t => ({ ...t, mode: 'single' }));
                        // Load distinct values when switching to single date mode
                        if (timeConfig.column && timeDistinctValues.length === 0) {
                          loadTimeDistinctValues();
                        }
                      }}
                      className={cn(
                        "flex-1 text-xs font-bold py-2.5 rounded-lg transition-all uppercase tracking-wider",
                        timeConfig.mode === 'single'
                          ? "bg-emerald-500 text-white shadow-md"
                          : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                    >
                      Single Date
                    </button>
                  </div>

                  {timeConfig.mode === 'range' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={timeConfig.start}
                        onChange={(e) => setTimeConfig(t => ({ ...t, start: e.target.value }))}
                        className="flex-1 text-sm px-4 py-2.5 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      />
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                        <ChevronRight size={14} />
                      </div>
                      <input
                        type="date"
                        value={timeConfig.end}
                        onChange={(e) => setTimeConfig(t => ({ ...t, end: e.target.value }))}
                        className="flex-1 text-sm px-4 py-2.5 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {loadingTimeValues ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 size={20} className="animate-spin text-emerald-500" />
                          <span className="ml-2 text-xs text-gray-500 font-medium">Loading available dates...</span>
                        </div>
                      ) : timeDistinctValues.length > 0 ? (
                        <>
                          <div className="flex items-center justify-between text-xs font-bold text-gray-600 dark:text-gray-400 px-1">
                            <span>{timeDistinctValues[0]}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-black text-sm">
                              {timeConfig.singleDate || timeDistinctValues[0]}
                            </span>
                            <span>{timeDistinctValues[timeDistinctValues.length - 1]}</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={timeDistinctValues.length - 1}
                            step={1}
                            value={timeConfig.singleDate
                              ? timeDistinctValues.indexOf(timeConfig.singleDate)
                              : 0}
                            onChange={(e) => {
                              const index = parseInt(e.target.value);
                              const selectedDate = timeDistinctValues[index];
                              if (selectedDate) {
                                setTimeConfig(t => ({ ...t, singleDate: selectedDate }));
                              }
                            }}
                            className="w-full h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 accent-emerald-500 cursor-pointer shadow-inner"
                          />
                          <div className="text-center">
                            <span className="text-[10px] text-gray-500 font-medium">
                              {timeDistinctValues.length} available date{timeDistinctValues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-xs text-gray-400">No dates available</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 4. METRIC */}
      {(form.spatialColumn && timeConfig.column) && (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
          <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg">
              <BarChart3 size={12} />
            </div>
            Metric <span className="text-gray-400 font-normal normal-case">(Optional)</span>
          </label>
          {currentStep >= STEPS.METRIC && (
            <div className="flex gap-3">
              <div className="relative flex-1">
                <select
                  value={form.valueColumn}
                  onChange={(e) => {
                    const col = e.target.value;
                    setForm(f => ({
                      ...f,
                      valueColumn: col,
                      aggregation: defaultAggregationForColumn(col, columns, f.aggregation)
                    }));
                  }}
                  className={cn(
                    "w-full text-sm py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm outline-none",
                    !form.valueColumn
                      ? "border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                      : "border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                  )}
                >
                  <option value="">Select metric...</option>
                  <option value={MAP_NO_METRIC}>No metric (uniform color)</option>
                  {columns.map(c => (
                    <option key={c.name} value={c.name}>
                      {c.name}{isNumericColumn(c.name, columns) ? '' : ' (category)'}
                    </option>
                  ))}
                </select>
                <BarChart3 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
              {hasMetric && (
                <select
                  value={form.aggregation}
                  onChange={(e) => setForm(f => ({ ...f, aggregation: e.target.value }))}
                  className="px-4 py-3 rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all cursor-pointer font-bold text-xs text-gray-900 dark:text-white uppercase tracking-wider shadow-sm min-w-[100px]"
                >
                  {metricIsNumeric && (
                    <>
                      <option value="sum">SUM</option>
                      <option value="avg">AVG</option>
                    </>
                  )}
                  <option value="min">MIN</option>
                  <option value="max">MAX</option>
                  <option value="count">COUNT</option>
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {/* 5. STYLE, FILTERS & NAMING (Visible after metric step) */}
      {metricReady && (
        <div className="space-y-6 animate-in fade-in slide-in-from-top-2 pt-6 border-t-2 border-gray-100 dark:border-gray-800">
          {/* Layer Name */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
              <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg">
                <Tag size={12} />
              </div>
              Layer Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={layerName}
                onChange={e => setLayerName(e.target.value)}
                placeholder="Give this layer a name..."
                className="w-full text-sm font-bold uppercase tracking-tight py-3 px-4 pl-10 rounded-2xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all placeholder:text-gray-400 text-gray-900 dark:text-white shadow-sm"
              />
              <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs font-black text-gray-900 dark:text-white uppercase tracking-widest">
                <div className="p-1.5 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg">
                  <Filter size={12} />
                </div>
                Data Filters
              </label>
              <button
                onClick={handleAddFilter}
                className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 px-3 py-1.5 rounded-xl transition-all"
              >
                <Plus size={14} /> Add Filter
              </button>
            </div>

            <div className="space-y-2.5">
              {form.filters.map((filter, idx) => (
                <div key={idx} className="flex gap-2 items-center p-3 bg-gradient-to-br from-gray-50/50 to-white dark:from-gray-900/30 dark:to-gray-900/50 rounded-xl border-2 border-gray-100 dark:border-gray-800 shadow-sm">
                  <select
                    value={filter.column}
                    onChange={e => updateFilter(idx, 'column', e.target.value)}
                    className="flex-1 text-sm py-2 px-3 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all cursor-pointer font-medium text-gray-900 dark:text-white shadow-sm"
                  >
                    <option value="">Column</option>
                    {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <select
                    value={filter.op}
                    onChange={e => updateFilter(idx, 'op', e.target.value)}
                    disabled={filter.column && getColType(filter.column) === 'STRING' && filter.op !== '='}
                    className={cn(
                      "w-16 text-xs font-bold py-2 px-2 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all cursor-pointer text-gray-900 dark:text-white uppercase tracking-wider shadow-sm",
                      filter.column && getColType(filter.column) === 'STRING' && filter.op !== '=' && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <option value="=">=</option>
                    {filter.column && getColType(filter.column) !== 'STRING' && (
                      <>
                        <option value="!=">!=</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                      </>
                    )}
                  </select>
                  <div className="flex-1 relative">
                    <input
                      list={`suggest - ${idx} `}
                      value={filter.values[0]}
                      onChange={e => updateFilter(idx, 'values', [e.target.value])}
                      className="w-full text-sm py-2 px-3 rounded-xl bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 focus:border-emerald-500 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-medium text-gray-900 dark:text-white shadow-sm"
                      placeholder="Value..."
                    />
                    <datalist id={`suggest - ${idx} `}>
                      {(filterOptions[idx] || []).map((v, i) => <option key={i} value={v} />)}
                    </datalist>
                    {loadingFilters[idx] && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 size={14} className="animate-spin text-emerald-500" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeFilter(idx)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {form.filters.length === 0 && (
                <div className="p-6 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">No filters added. Click "Add Filter" to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Fixed Footer Buttons */}
      <div className="sticky bottom-0 left-0 right-0 pt-6 pb-0 bg-gradient-to-t from-white via-white to-transparent dark:from-gray-950 dark:via-gray-950 border-t-2 border-gray-100 dark:border-gray-800 flex gap-3 mt-8 z-10">
        <button
          onClick={handleSubmit}
          disabled={!selectedDatasetId || !form.spatialColumn || !form.valueColumn || !timeConfig.column || savingArtefact}
          className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-wider rounded-xl shadow-lg hover:shadow-xl transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
        >
          {savingArtefact ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check size={18} />
              {initialState ? 'Update Layer' : 'Create Layer'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MapLayerBuilder;
